import {
  ESDialogActionButton,
  ESDialogActions,
  ESDialogContent,
  ESDialogTitle,
  useESSnackbar,
} from '@energy-stacks/core/ui';
import {
  OptimizedTourModel,
  useDeleteTourMutation,
} from '@energy-stacks/fleet/feature-tours-data';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toursApiErrors } from './toursApiErrors';
import { Dialog, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { FleetRoutes } from '@energy-stacks/fleet/shared';

type DeleteTourDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  tourId: OptimizedTourModel['id'];
  tourStatus: OptimizedTourModel['status'];
};

export const DeleteTourDialog: React.FC<DeleteTourDialogProps> = ({
  isOpen,
  onClose,
  tourId,
  tourStatus,
}) => {
  const [t] = useTranslation('tours');
  const [tShared] = useTranslation('shared');
  const { showSnackbar } = useESSnackbar();
  const [deleteTour, { isLoading }] = useDeleteTourMutation();
  const navigate = useNavigate();
  const { tourId: tourIdParam } = useParams<{ tourId: string }>();

  const handleDeleteTour = useCallback(() => {
    if (!tourId) {
      return;
    }
    return deleteTour(tourId)
      .unwrap()
      .then(() => {
        showSnackbar('success', 'deleteTourSuccess', 'tours');
        // In case tour is deleted from tour details page, we redirect to the tours overview
        if (tourIdParam) {
          navigate(FleetRoutes.Tours);
          return;
        }
        onClose();
      })
      .catch((error) => {
        const errorCode = toursApiErrors[error.data?.errorCode];
        showSnackbar(
          'error',
          errorCode ? `toursApiErrors.${errorCode}` : undefined,
          'tours'
        );
      });
  }, [deleteTour, navigate, onClose, showSnackbar, tourId, tourIdParam]);

  return (
    <Dialog open={isOpen} fullWidth={true} onClose={onClose}>
      <ESDialogTitle>{t('deleteTourConfirmTitle')}</ESDialogTitle>
      <ESDialogContent>
        <Typography variant="body1">
          {tourStatus === 'PLANNED'
            ? t('deleteTourConfirmMessage')
            : t('cannotDeleteTour')}
        </Typography>
      </ESDialogContent>
      <ESDialogActions>
        <ESDialogActionButton
          color="error"
          onClick={onClose}
          disabled={isLoading}
        >
          {tShared('cancelDeletion')}
        </ESDialogActionButton>
        <ESDialogActionButton
          variant="contained"
          color="error"
          loading={isLoading}
          disabled={tourStatus !== 'PLANNED'}
          onClick={handleDeleteTour}
        >
          {tShared('deleteActionButton')}
        </ESDialogActionButton>
      </ESDialogActions>
    </Dialog>
  );
};
